<template>
   <b-container fluid class="prospect-lookup px-3 px-xl-5 mt-5">
      <b-row class="mb-3">
         <b-col>
            <h2 class="prospect-lookup__heading">Credit Eligibility Researcher</h2>
            <p class="prospect-lookup__subtitle">
               Enter a company website URL to assess their likely R&amp;D tax credit eligibility.
               Your last 50 lookups are saved.
            </p>
            <p class="prospect-lookup__disclaimer">
               Always verify that the information matches your prospect. Results are based on
               publicly available content that may occasionally reflect a different company with a
               similar name or domain.
            </p>
         </b-col>
      </b-row>

      <b-row class="prospect-lookup__layout">
         <!-- Left column: form + history -->
         <b-col cols="12" lg="4" class="prospect-lookup__sidebar">
            <b-form @submit.prevent="onSubmit" class="mb-4">
               <b-form-group label="Company Website URL">
                  <b-input-group>
                     <b-form-input
                        v-model="url"
                        placeholder="https://example.com"
                        :disabled="submitting"
                        :state="urlError ? false : null"
                        trim
                     ></b-form-input>
                     <b-input-group-append>
                        <b-button
                           type="submit"
                           variant="primary"
                           :disabled="submitting || !url.trim() || hasPending"
                        >
                           {{ submitting ? 'Analyzing…' : 'Check Qualification' }}
                        </b-button>
                     </b-input-group-append>
                  </b-input-group>
                  <b-form-invalid-feedback :state="!urlError">{{
                     urlError
                  }}</b-form-invalid-feedback>
               </b-form-group>
            </b-form>

            <div v-if="lookups.length > 0" class="prospect-lookup__history-list">
               <p class="prospect-lookup__history-label">
                  Previous lookups (last {{ lookups.length }})
               </p>
               <div
                  v-for="lookup in lookups"
                  :key="lookup.id"
                  class="prospect-lookup__history-item"
                  :class="{
                     'prospect-lookup__history-item--active':
                        activeLookup && activeLookup.id === lookup.id,
                  }"
                  @click="selectLookup(lookup)"
               >
                  <div class="d-flex align-items-start justify-content-between">
                     <div class="prospect-lookup__history-url">{{ lookup.url }}</div>
                     <button
                        class="prospect-lookup__history-delete"
                        @click.stop="deleteLookup(lookup.id)"
                        aria-label="Remove"
                     >
                        &times;
                     </button>
                  </div>
                  <div class="d-flex align-items-center justify-content-between mt-1">
                     <small class="text-muted">{{ formatDate(lookup.created) }}</small>
                     <div class="d-flex align-items-center">
                        <span
                           v-if="lookup.result && lookup.result.qualificationScore"
                           class="prospect-lookup__history-score"
                           :class="scoreClass(lookup.result.qualificationScore)"
                           >{{ lookup.result.qualificationScore }}/10</span
                        >
                        <b-badge :variant="statusVariant(lookup.status)" class="ml-2">
                           {{ lookup.status }}
                        </b-badge>
                     </div>
                  </div>
               </div>
            </div>
         </b-col>

         <!-- Right column: result -->
         <b-col cols="12" lg="8" class="prospect-lookup__result">
            <!-- Pending: progress bar -->
            <div
               v-if="activeLookup && activeLookup.status === 'PENDING'"
               class="prospect-lookup__pending"
            >
               <template v-if="connectionError">
                  <b-alert show variant="warning" class="mt-2">
                     Lost connection to the server.
                     <b-link @click="retryConnection" class="ml-1">Retry</b-link>
                  </b-alert>
               </template>
               <template v-else>
                  <div class="prospect-lookup__progress-header">
                     <span
                        >Analyzing <strong>{{ activeLookup.url }}</strong></span
                     >
                  </div>
                  <b-progress
                     :max="activeLookup.progress ? activeLookup.progress.total + 1 : 4"
                     height="8px"
                     class="mt-2 mb-2"
                     animated
                  >
                     <b-progress-bar
                        :value="activeLookup.progress ? activeLookup.progress.step : 0"
                        variant="primary"
                     ></b-progress-bar>
                  </b-progress>
                  <small class="text-muted">
                     {{ activeLookup.progress ? activeLookup.progress.message : 'Initializing…' }}
                  </small>
               </template>
            </div>

            <!-- Error state -->
            <b-alert
               v-else-if="activeLookup && activeLookup.status === 'ERROR'"
               show
               variant="warning"
               class="mt-2"
            >
               The lookup could not be completed. Please try again or check that the URL is
               accessible.
            </b-alert>

            <!-- Done state -->
            <div v-else-if="activeLookup && activeLookup.status === 'DONE' && activeLookup.result">
               <ProspectLookupResult :result="activeLookup.result" />
            </div>

            <!-- Done but no result (unexpected) -->
            <b-alert
               v-else-if="activeLookup && activeLookup.status === 'DONE'"
               show
               variant="warning"
               class="mt-2"
            >
               The lookup completed but returned no result. Please try again.
            </b-alert>

            <!-- Empty state -->
            <div v-else-if="!activeLookup" class="prospect-lookup__empty">
               <p>Enter a company URL to get started.</p>
            </div>
         </b-col>
      </b-row>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import {prospectScoreTier} from '@/helpers/utils';
import ProspectLookupResult from './ProspectLookupResult.vue';

const POLL_INTERVAL_MS = 3000;

export default {
   name: 'ProspectLookupTool',

   components: {ProspectLookupResult},

   data() {
      return {
         url: '',
         urlError: '',
         submitting: false,
         pollTimer: null,
         polling: false,
         pollFailures: 0,
         connectionError: false,
      };
   },

   computed: {
      ...mapGetters({
         lookups: 'prospectLookup/lookups',
         activeLookup: 'prospectLookup/activeLookup',
      }),
      hasPending() {
         return this.lookups.some((l) => l.status === 'PENDING');
      },
   },

   async created() {
      try {
         await this.$store.dispatch('prospectLookup/loadHistory');
         const pending = this.lookups.filter((l) => l.status === 'PENDING');
         if (pending.length) {
            // Refresh all pending items immediately — they may have completed in a previous session
            await Promise.all(
               pending.map((l) =>
                  this.$store.dispatch('prospectLookup/pollLookup', l.id).catch(() => {})
               )
            );
            const stillPending = this.lookups.find((l) => l.status === 'PENDING');
            if (stillPending) {
               this.$store.commit('prospectLookup/setActiveLookupId', stillPending.id);
               this.startPolling();
            }
         }
      } catch (err) {
         // history load failure is non-fatal
      }
   },

   beforeDestroy() {
      this.stopPolling();
   },

   methods: {
      normalizeUrl(val) {
         return /^https?:\/\//i.test(val) ? val : `https://${val}`;
      },

      validateUrl(val) {
         try {
            new URL(this.normalizeUrl(val));
            return '';
         } catch {
            return 'Please enter a valid URL (e.g. example.com)';
         }
      },

      async onSubmit() {
         const trimmed = this.url.trim();
         this.urlError = this.validateUrl(trimmed);
         if (this.urlError) return;

         this.submitting = true;
         try {
            await this.$store.dispatch('prospectLookup/submitUrl', this.normalizeUrl(trimmed));
            this.url = '';
            this.startPolling();
         } catch (err) {
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Failed to submit URL for qualification lookup',
               seconds: 5,
            });
         } finally {
            this.submitting = false;
         }
      },

      async deleteLookup(id) {
         try {
            await this.$store.dispatch('prospectLookup/deleteLookup', id);
         } catch (err) {
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Failed to remove lookup',
               seconds: 5,
            });
         }
      },

      selectLookup(lookup) {
         this.$store.commit('prospectLookup/setActiveLookupId', lookup.id);
         if (this.hasPending) {
            if (!this.pollTimer) {
               this.connectionError = false;
               this.startPolling();
            }
         } else {
            this.stopPolling();
         }
      },

      async retryConnection() {
         this.connectionError = false;
         try {
            await this.$store.dispatch('prospectLookup/loadHistory');
            if (this.lookups.some((l) => l.status === 'PENDING')) {
               this.startPolling();
            }
         } catch {
            this.connectionError = true;
         }
      },

      startPolling() {
         this.stopPolling();
         this.pollFailures = 0;
         this.pollTimer = setInterval(async () => {
            if (this.polling) return;
            this.polling = true;
            try {
               if (!this.lookups.some((l) => l.status === 'PENDING')) {
                  this.stopPolling();
                  return;
               }
               await this.$store.dispatch('prospectLookup/loadHistory');
               this.pollFailures = 0;
               if (!this.lookups.some((l) => l.status === 'PENDING')) {
                  this.stopPolling();
               }
            } catch {
               this.pollFailures++;
               if (this.pollFailures >= 3) {
                  this.stopPolling();
                  this.connectionError = true;
               }
            } finally {
               this.polling = false;
            }
         }, POLL_INTERVAL_MS);
      },

      stopPolling() {
         if (this.pollTimer !== null) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
         }
      },

      statusVariant(status) {
         return {PENDING: 'secondary', DONE: 'success', ERROR: 'danger'}[status] || 'secondary';
      },

      scoreClass(score) {
         return `prospect-lookup__history-score--${prospectScoreTier(score)}`;
      },

      formatDate(iso) {
         if (!iso) return '';
         return new Date(iso).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
         });
      },
   },
};
</script>

<style lang="scss" scoped>
.prospect-lookup {
   border-top: 1px solid $gray-300;
   padding-top: 1.5rem;

   &__heading {
      font-size: 1.25rem;
      font-weight: 700;
   }

   &__subtitle {
      font-size: 0.875rem;
      color: $gray-600;
      margin-bottom: 0;
   }

   &__disclaimer {
      font-size: 0.8125rem;
      color: $gray-700;
      margin-top: 0.375rem;
      margin-bottom: 0;
   }

   &__layout {
      @media (min-width: 992px) {
         align-items: flex-start;
      }
   }

   &__sidebar {
      @media (min-width: 992px) {
         border-right: 1px solid $gray-300;
      }
   }

   &__history-list {
      @media (min-width: 992px) {
         max-height: calc(100vh - 260px);
         overflow-y: auto;
      }
   }

   &__history-label {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $gray-600;
      font-weight: 600;
      margin-bottom: 0.5rem;
   }

   &__history-item {
      padding: 0.5rem 0.75rem;
      border-radius: 4px;
      cursor: pointer;
      border: 1px solid transparent;

      &:hover {
         background-color: $gray-100;
      }

      &--active {
         border-color: $primary;
         background-color: $primary-100;
      }
   }

   &__history-url {
      font-size: 0.8125rem;
      word-break: break-all;
      font-weight: 500;
      flex: 1;
      margin-right: 0.25rem;
   }

   &__history-score {
      font-size: 0.75rem;
      font-weight: 700;

      &--high {
         color: $success;
      }
      &--mid {
         color: $warning;
      }
      &--low {
         color: $gray-500;
      }
   }

   &__history-delete {
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 0 0 0 0.25rem;
      line-height: 1;
      font-size: 1rem;
      color: $gray-600;
      cursor: pointer;
      opacity: 0;

      .prospect-lookup__history-item:hover & {
         opacity: 1;
      }

      &:hover {
         color: $gray-600;
      }
   }

   &__result {
      @media (min-width: 992px) {
         padding-left: 2rem;
         min-height: 480px;
      }
   }

   &__pending {
      padding: 2rem 0;
      font-size: 0.9375rem;
   }

   &__progress-header {
      display: flex;
      align-items: baseline;
      margin-bottom: 0.25rem;
   }

   &__empty {
      color: $gray-600;
      padding: 2rem 0;
      font-size: 0.9375rem;
   }
}
</style>
